"use client";

import { useState, useEffect } from "react";
import { X, Download } from "lucide-react";
import { useTranslations } from "@/lib/use-translations";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "pwa-install-dismissed";
const DISMISSED_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

export function PWAInstallBanner() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const t = useTranslations();

  useEffect(() => {
    // Only show on mobile devices
    const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    if (!isMobile) {
      return;
    }

    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently
    const dismissedAt = localStorage.getItem(DISMISSED_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10);
      if (elapsed < DISMISSED_DURATION) {
        return;
      }
      localStorage.removeItem(DISMISSED_KEY);
    }

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      setIsVisible(true);
    };

    const handleAppInstalled = () => {
      setIsVisible(false);
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;

    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;

    if (outcome === "accepted") {
      setIsVisible(false);
      setInstallPrompt(null);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(DISMISSED_KEY, Date.now().toString());
  };

  if (!isVisible || isInstalled) {
    return null;
  }

  return (
    <div className="bg-primary text-primary-foreground px-4 py-2.5 flex items-center gap-3 animate-fade-up">
      <Download className="w-5 h-5 shrink-0" />
      <p className="flex-1 text-sm font-medium">
        {t.pwa?.installPrompt || "Install SaySay for a better experience"}
      </p>
      <button
        type="button"
        onClick={handleInstall}
        className="px-3 py-1 text-xs font-semibold bg-primary-foreground text-primary rounded-md hover:bg-primary-foreground/90 transition-colors"
      >
        {t.pwa?.install || "Install"}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        className="p-1 hover:bg-primary-foreground/20 rounded transition-colors"
        aria-label={t.common?.close || "Close"}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
